import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import RegularImage from './RegularImage';
import ZoomImage from './ZoomImage';
import * as styles from './InnerImageZoom.css';

const cx = classNames.bind(styles);
let lastClick = 0;
const InnerImageZoom = ({
    moveType = 'pan',
    zoomType = 'click',
    src,
    sources,
    width,
    height,
    hasSpacer,
    imgAttributes = {},
    zoomSrc,
    zoomScale = 1,
    zoomPreload,
    fadeDuration = 150,
    fullscreenOnMobile,
    mobileBreakpoint = 640,
    deviceType = {},
    hideCloseButton,
    hideHint,
    className,
    afterZoomIn,
    afterZoomOut,
    isLandscape,
    updateWidth,
    imageOnLoad,
    shouldLoadZoomImgOnLoad,
}) => {
    const img = useRef(null);
    const zoomImg = useRef(null);
    const imgProps = useRef({});
    const [isTouch, setIsTouch] = useState(false);
    const [loadZoomImage, setLoadZoomImage] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isValidDrag, setIsValidDrag] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const [currentMoveType, setCurrentMoveType] = useState(moveType);
    const [left, setLeft] = useState(0);
    const [top, setTop] = useState(0);

    const handleMouseEnter = (e) => {
        setIsFading(false);
        zoomType === 'hover' && !isZoomed && handleClick(e);
    };

    const handleTouchStart = () => {
        setIsTouch(true);
        setIsFullscreen(getFullscreenStatus(fullscreenOnMobile, mobileBreakpoint));
        setCurrentMoveType('drag');
    };

    /* istanbul ignore next */
    const handleClick = (e) => {
        if (!isLandscape) {
            if (isZoomed) {
                !isValidDrag && zoomOut();

                return;
            }
            const pageX = deviceType.isDesktop
                ? get(e, 'pageX', 0)
                : get(e, 'changedTouches[0].pageX', 0);
            const pageY = deviceType.isDesktop
                ? get(e, 'pageY', 0)
                : get(e, 'changedTouches[0].pageY', 0);
            if (zoomImg.current) {
                handleLoad({ target: zoomImg.current });
                zoomIn(pageX, pageY);
            } else {
                imgProps.current.onLoadCallback = zoomIn.bind(this, pageX, pageY);
            }
        }
    };

    const doubleClickImage = (e) => {
        e.preventDefault();
        const date = new Date();
        const time = date.getTime();
        const time_between_taps = 400;
        const minTimeBtwTaps = 100;
        const timeTaken = time - lastClick;

        if (timeTaken > minTimeBtwTaps && timeTaken < time_between_taps) {
            handleClick(e);
        }
        lastClick = time;
    };

    const handleLoad = (e) => {
        const scaledDimensions = getScaledDimensions(e.target, zoomScale);

        zoomImg.current = e.target;
        zoomImg.current.setAttribute('width', scaledDimensions.width);
        zoomImg.current.setAttribute('height', scaledDimensions.height);

        imgProps.current.scaledDimensions = scaledDimensions;
        imgProps.current.bounds = getBounds(img?.current, false);
        imgProps.current.ratios = getRatios(imgProps?.current.bounds, scaledDimensions);

        if (imgProps?.current.onLoadCallback) {
            imgProps.current.onLoadCallback();
            imgProps.current.onLoadCallback = null;
        }
    };

    /* istanbul ignore next */
    const handleMouseMove = (e) => {
        let left = e.pageX - imgProps.current.offsets.x;
        let top = e.pageY - imgProps.current.offsets.y;

        left = Math.max(Math.min(left, imgProps.current.bounds?.width), 0);
        top = Math.max(Math.min(top, imgProps.current.bounds?.height), 0);

        setLeft(left * -imgProps.current.ratios.x);
        setTop(top * -imgProps.current.ratios.y);
    };

    /* istanbul ignore next */
    const handleDragStart = (e) => {
        const pageX = typeof e.pageX === 'number' ? e.pageX : e.changedTouches[0].pageX;
        const pageY = typeof e.pageY === 'number' ? e.pageY : e.changedTouches[0].pageY;
        imgProps.current.offsets = getOffsets(
            pageX,
            pageY,
            zoomImg.current.offsetLeft,
            zoomImg.current.offsetTop
        );

        setIsDragging(true);

        if (!isTouch) {
            imgProps.current.eventPosition = {
                x: e.pageX,
                y: e.pageY,
            };
        }
    };

    /* istanbul ignore next */
    const handleDragMove = useCallback((e) => {
        const pageX = typeof e.pageX === 'number' ? e.pageX : e.changedTouches[0].pageX;
        const pageY = typeof e.pageY === 'number' ? e.pageY : e.changedTouches[0].pageY;
        let left = pageX - imgProps.current.offsets.x;
        let top = pageY - imgProps.current.offsets.y;

        left = Math.max(
            Math.min(left, 0),
            (imgProps.current.scaledDimensions.width - imgProps.current.bounds.width) * -1
        );
        top = Math.max(
            Math.min(top, 0),
            (imgProps.current.scaledDimensions.height - imgProps.current.bounds.height) * -1
        );

        setLeft(left);
        setTop(top);
    }, []);

    /* istanbul ignore next */
    const handleDragEnd = (e) => {
        setIsDragging(false);

        if (!isTouch) {
            const moveX = Math.abs(e.pageX - imgProps.current.eventPosition.x);
            const moveY = Math.abs(e.pageY - imgProps.current.eventPosition.y);
            setIsValidDrag(moveX > 5 || moveY > 5);
        }
    };

    /* istanbul ignore next */
    const handleMouseLeave = (e) => {
        currentMoveType === 'drag' && isZoomed ? handleDragEnd(e) : handleClose(e);
    };

    /* istanbul ignore next */
    const handleClose = (e) => {
        if (!(!isTouch && e.target.classList.contains('ZoomContainer_close'))) {
            if (!isZoomed || isFullscreen || !fadeDuration) {
                handleFadeOut({}, true);
            } else {
                setIsFading(true);
            }
        }

        zoomOut();
    };

    /* istanbul ignore next */
    const handleFadeOut = (e, noTransition) => {
        if (noTransition || (e.propertyName === 'opacity' && img.current.contains(e.target))) {
            if ((zoomPreload && isTouch) || !zoomPreload) {
                if (deviceType.isDesktop) {
                    setIsZoomed(false);
                } else {
                    zoomImg.current = null;
                }
                imgProps.current = getDefaults();
            }

            setIsTouch(false);
            setIsFullscreen(false);
            setCurrentMoveType(moveType);
            setIsFading(false);
        }
    };

    /* istanbul ignore next */
    const initialMove = (pageX, pageY) => {
        imgProps.current.offsets = getOffsets(
            window.pageXOffset,
            window.pageYOffset,
            -imgProps.current.bounds?.left,
            -imgProps.current.bounds?.top
        );
        handleMouseMove({ pageX, pageY });
    };

    /* istanbul ignore next */
    const initialDrag = (pageX, pageY) => {
        let initialPageX =
            (pageX - (window.pageXOffset + imgProps.current.bounds.left)) *
            -imgProps.current.ratios.x;
        let initialPageY =
            (pageY - (window.pageYOffset + imgProps.current.bounds.top)) *
            -imgProps.current.ratios.y;

        initialPageX += isFullscreen ? (window.innerWidth - imgProps.current.bounds.width) / 2 : 0;
        initialPageY += isFullscreen
            ? (window.innerHeight - imgProps.current.bounds.height) / 2
            : 0;
        imgProps.current.bounds = getBounds(img.current, isFullscreen);
        imgProps.current.offsets = getOffsets(0, 0, 0, 0);

        handleDragMove({
            changedTouches: [
                {
                    pageX: initialPageX,
                    pageY: initialPageY,
                },
            ],
            preventDefault: () => {},
            stopPropagation: () => {},
        });
    };

    /* istanbul ignore next */
    const zoomIn = (pageX, pageY) => {
        setIsZoomed(true);
        currentMoveType === 'drag' ? initialDrag(pageX, pageY) : initialMove(pageX, pageY);
        afterZoomIn && afterZoomIn();
    };

    /* istanbul ignore next */
    const zoomOut = () => {
        setIsZoomed(false);
        afterZoomOut && afterZoomOut();
    };

    /* istanbul ignore next */
    const getDefaults = () => {
        return {
            onLoadCallback: null,
            bounds: {},
            offsets: {},
            ratios: {},
            eventPosition: {},
            scaledDimensions: {},
        };
    };

    const getBounds = (img, isFullscreen) => {
        if (isFullscreen) {
            return {
                width: window.innerWidth,
                height: window.innerHeight,
                left: 0,
                top: 0,
            };
        }

        return img?.getBoundingClientRect();
    };

    /* istanbul ignore next */
    const getOffsets = (pageX, pageY, left, top) => {
        return {
            x: pageX - left,
            y: pageY - top,
        };
    };

    const getRatios = (bounds, dimensions) => {
        return {
            x: (dimensions?.width - bounds?.width) / bounds?.width,
            y: (dimensions?.height - bounds?.height) / bounds?.height,
        };
    };

    const getFullscreenStatus = (fullscreenOnMobile, mobileBreakpoint) => {
        return (
            fullscreenOnMobile &&
            window.matchMedia &&
            window.matchMedia(`(max-width: ${mobileBreakpoint}px)`).matches
        );
    };

    const getScaledDimensions = (zoomImg, zoomScale) => {
        return {
            width: zoomImg.naturalWidth * zoomScale,
            height: zoomImg.naturalHeight * zoomScale,
        };
    };

    const zoomImageProps = {
        src: zoomSrc || src,
        fadeDuration: isFullscreen ? 0 : fadeDuration,
        top,
        left,
        isZoomed,
        onLoad: handleLoad,
        onDragStart: currentMoveType === 'drag' ? handleDragStart : null,
        onDragEnd: currentMoveType === 'drag' ? handleDragEnd : null,
        onClose: !hideCloseButton && currentMoveType === 'drag' ? handleClose : null,
        onFadeOut: isFading ? handleFadeOut : null,
    };

    useEffect(() => {
        imgProps.current = getDefaults();
        shouldLoadZoomImgOnLoad && setLoadZoomImage(true);
    }, []);

    /* istanbul ignore next */
    useEffect(() => {
        if (!zoomImg.current) {
            return;
        }

        const eventType = isTouch ? 'touchmove' : 'mousemove';

        if (isDragging) {
            zoomImg.current.addEventListener(eventType, handleDragMove, { passive: true });
        } else {
            zoomImg.current.removeEventListener(eventType, handleDragMove);
        }
    }, [isDragging, isTouch, handleDragMove]);
    return (
        <figure
            className={`${cx(
                'ZoomContainer',
                currentMoveType === 'drag' ? 'ZoomContainer_drag' : '',
                !isZoomed ? 'ZoomInCursorImage' : 'ZoomOutCursorImage',
                className || ''
            )} ${dt([
                'w-full',
                'm-0',
                'relative',
                'overflow-hidden',
                isZoomed ? 'smMd:h-screen' : 'h-full',
            ])}`}
            style={{
                width,
            }}
            ref={img}
            onTouchStart={isZoomed ? null : handleTouchStart}
            onTouchEnd={!deviceType.isDesktop ? doubleClickImage : null}
            onClick={
                deviceType.isDesktop
                    ? (e) => {
                          if (loadZoomImage) {
                              handleClick(e);
                          } else {
                              setLoadZoomImage(true);
                              handleClick(e);
                          }
                      }
                    : null
            }
            onMouseEnter={isTouch ? null : handleMouseEnter}
            onMouseMove={currentMoveType === 'drag' || !isZoomed ? null : handleMouseMove}
            onMouseLeave={isTouch ? null : handleMouseLeave}
        >
            <RegularImage
                src={src}
                sources={sources}
                width={width}
                height={height}
                hasSpacer={hasSpacer}
                imgAttributes={imgAttributes}
                fadeDuration={fadeDuration}
                isZoomed={isZoomed}
                isLandscape={isLandscape}
                deviceType={deviceType}
                updateWidth={updateWidth}
                imageOnLoad={imageOnLoad}
            />

            {loadZoomImage ? <ZoomImage {...zoomImageProps} /> : null}
        </figure>
    );
};

InnerImageZoom.propTypes = {
    moveType: PropTypes.string,
    zoomType: PropTypes.string,
    src: PropTypes.string.isRequired,
    sources: PropTypes.array,
    width: PropTypes.number,
    height: PropTypes.number,
    hasSpacer: PropTypes.bool,
    imgAttributes: PropTypes.object,
    zoomSrc: PropTypes.string,
    zoomScale: PropTypes.number,
    zoomPreload: PropTypes.bool,
    fadeDuration: PropTypes.number,
    fullscreenOnMobile: PropTypes.bool,
    mobileBreakpoint: PropTypes.number,
    hideCloseButton: PropTypes.bool,
    hideHint: PropTypes.bool,
    className: PropTypes.string,
    afterZoomIn: PropTypes.func,
    afterZoomOut: PropTypes.func,
    isLandscape: PropTypes.bool,
    deviceType: PropTypes.object,
    updateWidth: PropTypes.func,
    imageOnLoad: PropTypes.func,
    shouldLoadZoomImgOnLoad: PropTypes.bool,
};

export default InnerImageZoom;
